import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import SEO from './SEO';
import seoData from '../seoData';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  Wrench,
  Shield,
  Building2,
  Factory,
  Fuel,
  Zap,
  Droplets,
  Anchor,
  Settings,
  Timer,
  Flame,
  Award,
  Users,
  ArrowRight,
  CheckCircle2,
  CheckCircle,
  Phone,
  Mail,
  Clock,
  Network,
  Wind,
  Quote,
  Laptop,
  FileText,
  History,
  FolderOpen,
  FlaskConical,
  Cog } from
'lucide-react';
import { siteImages } from '../siteImages';
import { mockServices, mockIndustries, mockBenefits, mockCompanyInfo, mockTestimonials } from '../mock';
import Header from './Header';
import Footer from './Footer';
import TrustedBy from './TrustedBy';
import { useToast } from '../hooks/use-toast';

const iconMap = {
  wrench: Wrench,
  shield: Shield,
  pipe: Network,
  building: Building2,
  flask: Shield,
  wind: Wind,
  clock: Clock,
  factory: Factory,
  fuel: Fuel,
  zap: Zap,
  droplet: Droplets,
  anchor: Anchor,
  settings: Settings,
  timer: Timer,
  flame: Flame,
  award: Award,
  users: Users,
  laptop: Laptop,
  tank: Building2,
  roof: Building2,
  floor: Building2,
  beaker: FlaskConical,
  cog: Cog
};

// Animated Counter Component
const AnimatedCounter = ({ end, duration = 2000, suffix = '', prefix = '' }) => {
  const [count, setCount] = useState(0);
  const [hasAnimated, setHasAnimated] = useState(false);
  const counterRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !hasAnimated) {
          setHasAnimated(true);
          let startTime;
          const animate = (currentTime) => {
            if (!startTime) startTime = currentTime;
            const progress = Math.min((currentTime - startTime) / duration, 1);
            // Easing function for smooth animation
            const easeOutQuart = 1 - Math.pow(1 - progress, 4);
            setCount(Math.floor(easeOutQuart * end));
            if (progress < 1) {
              requestAnimationFrame(animate);
            } else {
              setCount(end);
            }
          };
          requestAnimationFrame(animate);
        }
      },
      { threshold: 0.5 }
    );

    if (counterRef.current) {
      observer.observe(counterRef.current);
    }

    return () => observer.disconnect();
  }, [end, duration, hasAnimated]);

  return (
    <span ref={counterRef}>
      {prefix}{count}{suffix}
    </span>
  );
};

// Typewriter Text Component
const TypewriterText = ({ text, speed = 30 }) => {
  const [displayedText, setDisplayedText] = useState('');
  const [hasAnimated, setHasAnimated] = useState(false);
  const textRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !hasAnimated) {
          setHasAnimated(true);
          let index = 0;
          const typeChar = () => {
            if (index < text.length) {
              setDisplayedText(text.slice(0, index + 1));
              index++;
              setTimeout(typeChar, speed);
            }
          };
          typeChar();
        }
      },
      { threshold: 0.3 }
    );

    if (textRef.current) {
      observer.observe(textRef.current);
    }

    return () => observer.disconnect();
  }, [text, speed, hasAnimated]);

  return (
    <span ref={textRef}>
      {displayedText}
      {displayedText.length < text.length && <span className="animate-pulse">|</span>}
    </span>
  );
};

const Home = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setFormData({ name: '', email: '', phone: '', company: '', message: '' });
        setShowSuccessModal(true);
      } else {
        toast({
          title: "Error",
          description: data.detail || "Failed to send message. Please try again.",
          variant: "destructive"
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to send message. Please try again or call us directly.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const serviceRoutes = {
    1: '/services/protective-coatings',
    2: '/services/hvac-repair',
    3: '/services/pipeline-repair',
    4: '/services/structural-repair',
    5: '/services/equipment-repair',
    6: '/services/industrial-cleaning',
    7: '/services/tank-repair',
    8: '/services/roof-repair',
    9: '/services/floor-repair'
  };

  const handleServiceClick = (serviceId) => {
    navigate(serviceRoutes[serviceId]);
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main>
      <SEO title={seoData.home?.title} description={seoData.home?.description} path="/" keywords={seoData.home?.keywords || []} />
      <section className="pt-28 pb-12 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-slate-900 via-slate-800 to-blue-900 relative overflow-hidden">
        {/* Background Image with Sepia Filter - Remove this div to revert to plain background */}
        <div 
          className="absolute inset-0 opacity-[0.35]"
          style={{
            backgroundImage: `url(${siteImages.hero.main})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            filter: 'sepia(60%) saturate(80%)'
          }}
        ></div>
        {/* Gradient Overlay for depth */}
        <div className="absolute inset-0 bg-gradient-to-b from-slate-900/40 via-transparent to-slate-900/60"></div>
        
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-0">
            <h1 className="!font-bold !text-5xl mb-1 text-white leading-tight drop-shadow-lg">
              Infrastructure - Engineering - Construction
            </h1>
            <p className="!text-xl text-slate-200 max-w-4xl mx-auto mb-8">
              BCS, a division of Corrosion Analytics™, provides nationwide general contracting services for capital improvements, structural rehabilitation, and asset protection across industry.
            </p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-center">
            <div className="relative flex items-center justify-center" style={{ perspective: '1000px' }}>
              {/* BCS Logo with animated blueprint annotations */}
              <div className="relative w-[520px] h-[250px]">
                <img 
                  src="/bcs-logo-letters.png" 
                  alt="BCS - Coatings and Repair Services for Assets and Infrastructure" 
                  width="520"
                  height="250"
                  className="absolute inset-0 w-full h-full object-contain drop-shadow-2xl z-0"
                />
                <img 
                  src="/bcs-logo-annotations.png" 
                  alt=""
                  width="520"
                  height="250"
                  className="absolute inset-0 w-full h-full object-contain blueprint-draw z-10"
                  aria-hidden="true"
                />
              </div>
            </div>
            
            <div>
              <div className="flex flex-col sm:flex-row gap-3 mb-6 mt-4">
                <Button
                  size="lg"
                  className="bg-green-500 hover:bg-green-600 text-white font-semibold px-8 py-6 text-lg shadow-lg shadow-green-500/30"
                  onClick={() => window.location.href = 'tel:+19493719948'}>
                  <Phone className="mr-2" size={20} />
                  (949) 371-9948
                </Button>
                <Button
                  size="lg"
                  className="bg-blue-900 hover:bg-blue-800 text-white font-semibold px-8 py-6 text-lg shadow-lg shadow-blue-900/30"
                  onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}>
                  Get In Touch Today
                  <ArrowRight className="ml-2" size={20} />
                </Button>
              </div>
              <div className="grid grid-cols-3 gap-3 text-sm mb-6">
                <div className="flex items-center bg-white/10 backdrop-blur-sm rounded-full px-3 py-1.5 justify-center">
                  <CheckCircle2 className="text-green-400 mr-2 flex-shrink-0" size={18} />
                  <span className="text-white font-medium">Structural Repairs</span>
                </div>
                <div className="flex items-center bg-white/10 backdrop-blur-sm rounded-full px-3 py-1.5 justify-center">
                  <CheckCircle2 className="text-green-400 mr-2 flex-shrink-0" size={18} />
                  <span className="text-white font-medium">QA/QC Inspection</span>
                </div>
                <div className="flex items-center bg-white/10 backdrop-blur-sm rounded-full px-3 py-1.5 justify-center">
                  <CheckCircle2 className="text-green-400 mr-2 flex-shrink-0" size={18} />
                  <span className="text-white font-medium">Corrosion Systems</span>
                </div>
                <div className="flex items-center bg-white/10 backdrop-blur-sm rounded-full px-3 py-1.5 justify-center">
                  <CheckCircle2 className="text-green-400 mr-2 flex-shrink-0" size={18} />
                  <span className="text-white font-medium">Mechanical Repairs</span>
                </div>
                <div className="flex items-center bg-white/10 backdrop-blur-sm rounded-full px-3 py-1.5 justify-center">
                  <CheckCircle2 className="text-green-400 mr-2 flex-shrink-0" size={18} />
                  <span className="text-white font-medium">Field Services</span>
                </div>
                <div className="flex items-center bg-white/10 backdrop-blur-sm rounded-full px-3 py-1.5 justify-center">
                  <CheckCircle2 className="text-green-400 mr-2 flex-shrink-0" size={18} />
                  <span className="text-white font-medium">Protective Coatings</span>
                </div>
              </div>
              
              {/* Credentials Section */}
              <div className="bg-white/10 backdrop-blur-md rounded-xl p-4 border border-white/20 shadow-xl">
                <div className="flex items-center gap-2 mb-3">
                  <Award className="text-amber-400" size={18} />
                  <p className="text-sm text-white font-semibold uppercase tracking-wide">Certifications & Credentials</p>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="flex items-center gap-2 text-sm text-white/90 bg-white/10 rounded-lg px-2.5 py-1.5 col-span-2">
                    <CheckCircle2 className="text-green-400 flex-shrink-0" size={14} />
                    <span className="font-medium">CSLB License No. 1066525 &nbsp;|&nbsp; Class A &amp; C-33</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-white/90 bg-white/10 rounded-lg px-2.5 py-1.5">
                    <CheckCircle2 className="text-green-400 flex-shrink-0" size={14} />
                    <span className="font-medium">DIR No. 2000002659</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-white/90 bg-white/10 rounded-lg px-2.5 py-1.5">
                    <CheckCircle2 className="text-green-400 flex-shrink-0" size={14} />
                    <span className="font-medium">AMPP/NACE Certified</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-white/90 bg-white/10 rounded-lg px-2.5 py-1.5">
                    <CheckCircle2 className="text-green-400 flex-shrink-0" size={14} />
                    <span className="font-medium">DBIDS Gov Contractor</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-white/90 bg-white/10 rounded-lg px-2.5 py-1.5">
                    <CheckCircle2 className="text-green-400 flex-shrink-0" size={14} />
                    <span className="font-medium">TWIC Credentialed</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-white/90 bg-white/10 rounded-lg px-2.5 py-1.5">
                    <CheckCircle2 className="text-green-400 flex-shrink-0" size={14} />
                    <span className="font-medium">DUNS No. 080247052</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-white/90 bg-white/10 rounded-lg px-2.5 py-1.5">
                    <CheckCircle2 className="text-green-400 flex-shrink-0" size={14} />
                    <span className="font-medium">WBENC No. WBE2600603</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-blue-100 text-blue-900 hover:bg-blue-100">Our Services</Badge>
            <h2 className="text-4xl font-bold text-slate-900 mb-4">
              Comprehensive Epoxy Repair Solutions
            </h2>
            <p className="max-w-3xl !text-xl mx-auto text-gray-600">From planned maintenance to emergency repairs, we provide proven solutions for industrial equipment and infrastructure.

            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {mockServices.map((service) => {
              const IconComponent = iconMap[service.icon] || Wrench;
              const serviceImage = siteImages.services[service.imageKey];
              return (
                <Card
                  key={service.id}
                  className="hover:shadow-lg transition-all duration-300 border-slate-200 cursor-pointer group overflow-hidden"
                  onClick={() => handleServiceClick(service.id)}>
                  
                  {/* Service Image */}
                  <div className="h-40 overflow-hidden">
                    <img 
                      src={serviceImage?.url} 
                      alt={serviceImage?.alt || service.title}
                      width="400"
                      height="160"
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>

                  <CardHeader className="pt-4">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mb-3">
                      <IconComponent className="text-blue-900" size={20} />
                    </div>
                    <CardTitle className="text-slate-900 text-lg">{service.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-gray-600">{service.description}</CardDescription>
                    {service.description2 && (
                      <CardDescription className="text-gray-600 mt-2">{service.description2}</CardDescription>
                    )}
                  </CardContent>
                </Card>);

            })}
          </div>
        </div>
      </section>

      {/* Asset Life-Extension Section */}
      <section id="asset-life-extension" className="py-24 px-4 sm:px-6 lg:px-8 bg-slate-50 relative overflow-hidden">
        <div className="max-w-7xl mx-auto relative z-10">
          {/* Header */}
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-green-100 text-green-800 border border-green-200 hover:bg-green-100">Preventative Maintenance</Badge>
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
              The <span className="text-green-600">SMART</span> Program
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Asset Life Extension Through Preventative Maintenance — Our comprehensive approach extends the service life of your critical infrastructure by years, not months.
            </p>
          </div>

          {/* SMART Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-16">
            {/* S Card */}
            <div 
              onClick={() => navigate('/maintenance/systematic-inspections')}
              className="group relative bg-white rounded-2xl overflow-hidden border border-gray-200 hover:border-green-500 transition-all duration-300 hover:shadow-xl hover:shadow-green-500/10 cursor-pointer"
            >
              <div className="aspect-[4/3] relative overflow-hidden">
                <img 
                  src="https://customer-assets.emergentagent.com/job_bcs-epoxy-1/artifacts/evv1bv4y_36EE2360-ABA8-4787-B645-059F844718E9_1_105_c.jpeg" 
                  alt="Coating thickness measurement inspection" 
                  width="400"
                  height="300"
                  loading="lazy"
                  className="w-full h-full object-cover object-bottom group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-transparent to-transparent"></div>
                <div className="absolute top-4 left-4 w-12 h-12 bg-green-600 rounded-xl flex items-center justify-center shadow-lg">
                  <span className="text-white font-bold text-2xl">S</span>
                </div>
              </div>
              <div className="p-5">
                <h3 className="font-bold text-slate-900 text-lg mb-2">Systematic Inspections</h3>
                <p className="text-gray-600 text-sm leading-relaxed">Regular assessments to identify issues before failure</p>
              </div>
            </div>

            {/* M Card */}
            <div 
              onClick={() => navigate('/maintenance/material-specific-solutions')}
              className="group relative bg-white rounded-2xl overflow-hidden border border-gray-200 hover:border-green-500 transition-all duration-300 hover:shadow-xl hover:shadow-green-500/10 cursor-pointer"
            >
              <div className="aspect-[4/3] relative overflow-hidden">
                <img 
                  src="https://customer-assets.emergentagent.com/job_bcs-epoxy-1/artifacts/o4okjspj_iasdfgmage.png" 
                  alt="Material-specific coating solutions" 
                  width="400"
                  height="300"
                  loading="lazy"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-transparent to-transparent"></div>
                <div className="absolute top-4 left-4 w-12 h-12 bg-green-600 rounded-xl flex items-center justify-center shadow-lg">
                  <span className="text-white font-bold text-2xl">M</span>
                </div>
              </div>
              <div className="p-5">
                <h3 className="font-bold text-slate-900 text-lg mb-2">Material-Specific Solutions</h3>
                <p className="text-gray-600 text-sm leading-relaxed">Tailored repairs matched to your equipment</p>
              </div>
            </div>

            {/* A Card */}
            <div 
              onClick={() => navigate('/maintenance/active-corrosion-control')}
              className="group relative bg-white rounded-2xl overflow-hidden border border-gray-200 hover:border-green-500 transition-all duration-300 hover:shadow-xl hover:shadow-green-500/10 cursor-pointer"
            >
              <div className="aspect-[4/3] relative overflow-hidden">
                <img 
                  src="https://customer-assets.emergentagent.com/job_bcs-epoxy-1/artifacts/qg0y3n1j_8BA47EF2-9F0D-4476-83F3-AA4783BDBD53_1_102_o.jpeg" 
                  alt="Corrosion control and protective coating" 
                  width="400"
                  height="300"
                  loading="lazy"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-transparent to-transparent"></div>
                <div className="absolute top-4 left-4 w-12 h-12 bg-green-600 rounded-xl flex items-center justify-center shadow-lg">
                  <span className="text-white font-bold text-2xl">A</span>
                </div>
              </div>
              <div className="p-5">
                <h3 className="font-bold text-slate-900 text-lg mb-2">Active Corrosion Control</h3>
                <p className="text-gray-600 text-sm leading-relaxed">Proactive protection against degradation</p>
              </div>
            </div>

            {/* R Card */}
            <div 
              onClick={() => navigate('/maintenance/reliable-repair-methods')}
              className="group relative bg-white rounded-2xl overflow-hidden border border-gray-200 hover:border-green-500 transition-all duration-300 hover:shadow-xl hover:shadow-green-500/10 cursor-pointer"
            >
              <div className="aspect-[4/3] relative overflow-hidden">
                <img 
                  src="https://customer-assets.emergentagent.com/job_bcs-epoxy-1/artifacts/dlknthza_4AF28A47-6B05-45C9-8E95-98A50BE8381B_1_102_o.jpeg" 
                  alt="Industrial technician performing repair" 
                  width="400"
                  height="300"
                  loading="lazy"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-transparent to-transparent"></div>
                <div className="absolute top-4 left-4 w-12 h-12 bg-green-600 rounded-xl flex items-center justify-center shadow-lg">
                  <span className="text-white font-bold text-2xl">R</span>
                </div>
              </div>
              <div className="p-5">
                <h3 className="font-bold text-slate-900 text-lg mb-2">Reliable Repair Methods</h3>
                <p className="text-gray-600 text-sm leading-relaxed">Proven techniques for lasting results</p>
              </div>
            </div>

            {/* T Card */}
            <div 
              onClick={() => navigate('/maintenance/total-asset-lifecycle')}
              className="group relative bg-white rounded-2xl overflow-hidden border border-gray-200 hover:border-green-500 transition-all duration-300 hover:shadow-xl hover:shadow-green-500/10 cursor-pointer"
            >
              <div className="aspect-[4/3] relative overflow-hidden">
                <img 
                  src="https://customer-assets.emergentagent.com/job_bcs-epoxy-1/artifacts/hlry3vfg_8EC7E290-00C8-49AC-8F37-2D65410C533B_1_102_o.jpeg" 
                  alt="Total asset lifecycle - installation to retirement" 
                  width="400"
                  height="300"
                  loading="lazy"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-transparent to-transparent"></div>
                <div className="absolute top-4 left-4 w-12 h-12 bg-green-600 rounded-xl flex items-center justify-center shadow-lg">
                  <span className="text-white font-bold text-2xl">T</span>
                </div>
              </div>
              <div className="p-5">
                <h3 className="font-bold text-slate-900 text-lg mb-2">Total Asset Lifecycle</h3>
                <p className="text-gray-600 text-sm leading-relaxed">Comprehensive care from installation to retirement</p>
              </div>
            </div>
          </div>

          {/* Program Benefits Row */}
          <div className="bg-gradient-to-r from-green-700 to-green-600 rounded-2xl p-8 shadow-xl">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8 items-center">
              {/* Benefits */}
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Timer className="text-white" size={28} />
                </div>
                <div>
                  <p className="font-bold text-white text-lg">5-15 Years</p>
                  <p className="text-green-100 text-sm">Extended Equipment Life</p>
                </div>
              </div>
              
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Settings className="text-white" size={28} />
                </div>
                <div>
                  <p className="font-bold text-white text-lg">24/7 Ready</p>
                  <p className="text-green-100 text-sm">Reduced Downtime</p>
                </div>
              </div>
              
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Award className="text-white" size={28} />
                </div>
                <div>
                  <p className="font-bold text-white text-lg">40-60%</p>
                  <p className="text-green-100 text-sm">Cost Savings</p>
                </div>
              </div>

              {/* CTA Button */}
              <div className="flex justify-center md:justify-end">
                <Button
                  size="lg"
                  className="bg-white hover:bg-gray-100 text-green-700 font-semibold px-8 shadow-lg transition-all duration-300"
                  onClick={() => navigate('/services/asset-life-extension')}>
                  Learn More About SMART
                  <ArrowRight className="ml-2" size={20} />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Warranties Section */}
      <section id="warranties" className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-900 to-blue-800 text-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-white text-blue-900 hover:bg-white">Our Warranties</Badge>
            <h2 className="text-4xl font-bold mb-4">
              Backed by Comprehensive Warranties
            </h2>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto">
              We stand behind our work with industry-leading warranty coverage that protects your investment.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            <Card className="bg-white/10 border-white/20 backdrop-blur-sm">
              <CardContent className="pt-6">
                <div className="flex items-start mb-4">
                  <div className="w-12 h-12 bg-green-500 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <CheckCircle size={24} className="text-white" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-white mb-2">Workmanship Warranty</h3>
                    <p className="text-blue-100">
                      All installations are backed by our comprehensive workmanship warranty, ensuring proper application and long-term performance.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white/10 border-white/20 backdrop-blur-sm">
              <CardContent className="pt-6">
                <div className="flex items-start mb-4">
                  <div className="w-12 h-12 bg-green-500 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <Shield size={24} className="text-white" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-white mb-2">Material Warranty</h3>
                    <p className="text-blue-100">
                      Industry-leading epoxy composites and coating systems come with manufacturer warranties for proven durability.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-8 border border-white/20">
            <h3 className="text-2xl font-bold text-white mb-6 text-center">What Our Warranties Cover</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex items-start">
                <CheckCircle className="text-green-400 mr-3 mt-1 flex-shrink-0" size={20} />
                <div>
                  <p className="text-white font-semibold mb-1">Application Quality</p>
                  <p className="text-blue-100 text-sm">Proper surface preparation and material application per manufacturer specifications</p>
                </div>
              </div>
              <div className="flex items-start">
                <CheckCircle className="text-green-400 mr-3 mt-1 flex-shrink-0" size={20} />
                <div>
                  <p className="text-white font-semibold mb-1">Material Performance</p>
                  <p className="text-blue-100 text-sm">Coating adhesion, durability, and chemical resistance as specified</p>
                </div>
              </div>
              <div className="flex items-start">
                <CheckCircle className="text-green-400 mr-3 mt-1 flex-shrink-0" size={20} />
                <div>
                  <p className="text-white font-semibold mb-1">Repair Integrity</p>
                  <p className="text-blue-100 text-sm">Structural repairs meet or exceed original design specifications</p>
                </div>
              </div>
              <div className="flex items-start">
                <CheckCircle className="text-green-400 mr-3 mt-1 flex-shrink-0" size={20} />
                <div>
                  <p className="text-white font-semibold mb-1">Documentation</p>
                  <p className="text-blue-100 text-sm">Complete QA/QC records and warranty documentation provided</p>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-12 text-center">
            <p className="text-xl text-blue-100 mb-6">
              Contact us for specific warranty terms and conditions for your project.
            </p>
            <Button
              size="lg"
              className="bg-white text-blue-900 hover:bg-gray-100"
              onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}>

              Request Warranty Information
              <ArrowRight className="ml-2" size={20} />
            </Button>
          </div>
        </div>
      </section>

      {/* Industries Section */}
      <section id="industries" className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-green-100 text-green-900 hover:bg-green-100">Industries We Serve</Badge>
            <h2 className="text-4xl font-bold text-slate-900 mb-4">
              Trusted Across Multiple Industries
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Corrosion represents one of the largest ongoing costs to industrial asset owners. We deliver protection and repair systems designed for long-term performance.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {mockIndustries.map((industry) => {
              const IconComponent = iconMap[industry.icon] || Factory;
              const industryImage = siteImages.industries[industry.imageKey];
              // Adjust object position for marine to show more of the platform
              const imageClass = industry.imageKey === 'marine' 
                ? 'w-full h-full object-cover object-bottom group-hover:scale-105 transition-transform duration-300'
                : 'w-full h-full object-cover group-hover:scale-105 transition-transform duration-300';
              
              // Map industry imageKey to route paths
              const industryRoutes = {
                manufacturing: '/industries/manufacturing',
                oilGas: '/industries/oil-and-gas',
                powerGeneration: '/industries/power',
                waterTreatment: '/industries/water-wastewater',
                marine: '/industries/marine',
                generalIndustrial: '/industries/facilities-maintenance',
                petrochemical: '/industries/petrochemical',
                generalIndustry: '/industries/general-industry',
                windPower: '/industries/wind-power'
              };
              
              const industryPath = industryRoutes[industry.imageKey] || '/industries';
              
              return (
                <Card 
                  key={industry.id} 
                  className="hover:shadow-lg transition-all duration-300 bg-white border-slate-200 group overflow-hidden cursor-pointer"
                  onClick={() => navigate(industryPath)}
                  data-testid={`industry-card-${industry.imageKey}`}
                >
                  {/* Industry Image */}
                  {industryImage && (
                    <div className="h-36 overflow-hidden">
                      <img 
                        src={industryImage.url} 
                        alt={industryImage.alt || industry.name}
                        width="400"
                        height="144"
                        loading="lazy"
                        className={imageClass}
                      />
                    </div>
                  )}
                  <CardHeader className={industryImage ? "pt-4" : ""}>
                    <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center mb-3">
                      <IconComponent className="text-green-700" size={20} />
                    </div>
                    <CardTitle className="text-slate-900 group-hover:text-blue-900 transition-colors">{industry.name}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-gray-600">{industry.description}</CardDescription>
                  </CardContent>
                </Card>);

            })}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section id="why-choose-bcs" className="py-20 px-4 sm:px-6 lg:px-8 bg-blue-900 text-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-6">
              Why Choose BCS?
            </h2>
            <p className="max-w-4xl text-lg mx-auto text-blue-100 leading-relaxed">
              BCS is built on 25+ years of hands-on field experience supporting coating, lining, and repair work where proper execution mattered more than sales or shortcuts. After repeatedly seeing projects succeed—or fail—based on preparation and discipline, BCS was formed to deliver repair work the right way from start to finish.
            </p>
            <p className="max-w-4xl text-lg mx-auto text-blue-100 leading-relaxed mt-4">
              Today, BCS brings that field-driven approach to projects nationwide, focusing on controlled installation, accountability, and results that last beyond project closeout.
            </p>
          </div>

          {/* Core Benefits Grid - First 4 */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
            {mockBenefits.slice(0, 4).map((benefit) => {
              const IconComponent = iconMap[benefit.icon] || Award;
              return (
                <div key={benefit.id} className="text-center">
                  <div className="w-16 h-16 bg-green-500 rounded-lg flex items-center justify-center mx-auto mb-4">
                    <IconComponent size={32} />
                  </div>
                  <h3 className="text-xl font-semibold mb-2">{benefit.title}</h3>
                  <p className="text-blue-100">{benefit.description}</p>
                </div>
              );
            })}
          </div>

          {/* Customer Portal Feature Section */}
          <div className="bg-blue-800/50 rounded-2xl p-8 md:p-12 border border-blue-700">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-green-500 rounded-lg flex items-center justify-center">
                    <Laptop size={24} />
                  </div>
                  <h3 className="text-2xl font-bold">BCS Customer Portal</h3>
                </div>
                <p className="text-blue-100 text-lg mb-6">
                  Get exclusive access to our Customer Portal—a powerful tool designed to keep you informed and in control of every project from start to finish.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <CheckCircle className="text-green-400 mt-1 flex-shrink-0" size={20} />
                    <span className="text-blue-100"><strong className="text-white">Real-Time Project Tracking</strong> — Monitor progress, milestones, and completion status for all your active repairs</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="text-green-400 mt-1 flex-shrink-0" size={20} />
                    <span className="text-blue-100"><strong className="text-white">Case Study Documentation</strong> — Create detailed records of each repair with before/after photos, materials used, and outcomes</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="text-green-400 mt-1 flex-shrink-0" size={20} />
                    <span className="text-blue-100"><strong className="text-white">Complete Repair History</strong> — Access your full maintenance record for better planning and asset management</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="text-green-400 mt-1 flex-shrink-0" size={20} />
                    <span className="text-blue-100"><strong className="text-white">Document Library</strong> — Download proposals, QA reports, warranties, and invoices anytime</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="text-green-400 mt-1 flex-shrink-0" size={20} />
                    <span className="text-blue-100"><strong className="text-white">Direct Support Access</strong> — Connect with our technical team for questions or emergency service requests</span>
                  </li>
                </ul>
              </div>
              <div className="grid grid-cols-2 gap-4">
                {/* Project Reports Box with Popup */}
                <div className="relative group">
                  <div className="bg-blue-700/50 rounded-xl p-6 text-center cursor-pointer transition-all duration-300 hover:bg-blue-600/60 hover:scale-105 hover:shadow-lg">
                    <FileText className="mx-auto mb-3 text-green-400" size={32} />
                    <p className="font-semibold">Project Reports</p>
                    <p className="text-sm text-blue-200 mt-1">Detailed documentation</p>
                  </div>
                  {/* Popup */}
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-3 w-80 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 z-50">
                    <div className="bg-white rounded-xl shadow-2xl border border-gray-100 overflow-hidden">
                      <div className="bg-gradient-to-r from-blue-900 to-blue-800 px-4 py-3 flex items-center gap-2">
                        <img src="/bcs-logo.png" alt="BCS Logo" width="24" height="24" loading="lazy" className="h-6 w-auto" />
                        <span className="text-white font-semibold text-sm">Project Report Example</span>
                      </div>
                      <div className="p-4">
                        <div className="border border-gray-200 rounded-lg p-3 mb-3 bg-gray-50">
                          <div className="flex justify-between items-center mb-2">
                            <span className="text-xs font-semibold text-blue-900">PUMP BEARING RESTORATION</span>
                            <span className="text-xs text-gray-500">REF: BCS-2026-0142</span>
                          </div>
                          <div className="text-xs text-gray-600 space-y-1">
                            <p><strong>Client:</strong> Pacific Manufacturing</p>
                            <p><strong>Equipment:</strong> Centrifugal Pump #7</p>
                            <p><strong>Status:</strong> <span className="text-green-600 font-medium">Completed</span></p>
                          </div>
                        </div>
                        <div className="flex gap-2 text-xs">
                          <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded">PDF Report</span>
                          <span className="bg-green-100 text-green-800 px-2 py-1 rounded">Photos</span>
                          <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded">Specs</span>
                        </div>
                      </div>
                    </div>
                    <div className="absolute left-1/2 -translate-x-1/2 -bottom-2 w-4 h-4 bg-white rotate-45 border-r border-b border-gray-100"></div>
                  </div>
                </div>

                {/* Repair History Box with Popup */}
                <div className="relative group">
                  <div className="bg-blue-700/50 rounded-xl p-6 text-center cursor-pointer transition-all duration-300 hover:bg-blue-600/60 hover:scale-105 hover:shadow-lg">
                    <History className="mx-auto mb-3 text-green-400" size={32} />
                    <p className="font-semibold">Repair History</p>
                    <p className="text-sm text-blue-200 mt-1">Complete records</p>
                  </div>
                  {/* Popup */}
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-3 w-80 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 z-50">
                    <div className="bg-white rounded-xl shadow-2xl border border-gray-100 overflow-hidden">
                      <div className="bg-gradient-to-r from-blue-900 to-blue-800 px-4 py-3 flex items-center gap-2">
                        <img src="/bcs-logo.png" alt="BCS Logo" width="24" height="24" loading="lazy" className="h-6 w-auto" />
                        <span className="text-white font-semibold text-sm">Repair History Timeline</span>
                      </div>
                      <div className="p-4">
                        <div className="space-y-3">
                          <div className="flex gap-3 items-start">
                            <div className="w-2 h-2 rounded-full bg-green-500 mt-1.5 flex-shrink-0"></div>
                            <div className="text-xs">
                              <p className="font-semibold text-gray-900">Jan 2026 - Pump Overhaul</p>
                              <p className="text-gray-500">Bearing housing rebuilt, warranty active</p>
                            </div>
                          </div>
                          <div className="flex gap-3 items-start">
                            <div className="w-2 h-2 rounded-full bg-blue-500 mt-1.5 flex-shrink-0"></div>
                            <div className="text-xs">
                              <p className="font-semibold text-gray-900">Oct 2025 - Pipe Wrap Repair</p>
                              <p className="text-gray-500">6" line reinforced, no hot work</p>
                            </div>
                          </div>
                          <div className="flex gap-3 items-start">
                            <div className="w-2 h-2 rounded-full bg-gray-400 mt-1.5 flex-shrink-0"></div>
                            <div className="text-xs">
                              <p className="font-semibold text-gray-900">Aug 2025 - Tank Coating</p>
                              <p className="text-gray-500">Interior lining applied</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="absolute left-1/2 -translate-x-1/2 -bottom-2 w-4 h-4 bg-white rotate-45 border-r border-b border-gray-100"></div>
                  </div>
                </div>

                {/* Case Studies Box with Popup */}
                <div className="relative group">
                  <div className="bg-blue-700/50 rounded-xl p-6 text-center cursor-pointer transition-all duration-300 hover:bg-blue-600/60 hover:scale-105 hover:shadow-lg">
                    <FolderOpen className="mx-auto mb-3 text-green-400" size={32} />
                    <p className="font-semibold">Case Studies</p>
                    <p className="text-sm text-blue-200 mt-1">Document projects</p>
                  </div>
                  {/* Popup */}
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-3 w-80 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 z-50">
                    <div className="bg-white rounded-xl shadow-2xl border border-gray-100 overflow-hidden">
                      <div className="bg-gradient-to-r from-blue-900 to-blue-800 px-4 py-3 flex items-center gap-2">
                        <img src="/bcs-logo.png" alt="BCS Logo" width="24" height="24" loading="lazy" className="h-6 w-auto" />
                        <span className="text-white font-semibold text-sm">Case Study Preview</span>
                      </div>
                      <div className="p-4">
                        <div className="bg-gradient-to-br from-blue-50 to-green-50 rounded-lg p-3 mb-3">
                          <p className="text-xs font-bold text-blue-900 mb-1">HEAT EXCHANGER RESTORATION</p>
                          <p className="text-xs text-gray-600 mb-2">Saved $180,000 vs. replacement</p>
                          <div className="grid grid-cols-3 gap-2 text-center">
                            <div>
                              <p className="text-lg font-bold text-green-600">48hr</p>
                              <p className="text-[10px] text-gray-500">Turnaround</p>
                            </div>
                            <div>
                              <p className="text-lg font-bold text-blue-600">5yr</p>
                              <p className="text-[10px] text-gray-500">Warranty</p>
                            </div>
                            <div>
                              <p className="text-lg font-bold text-purple-600">Zero</p>
                              <p className="text-[10px] text-gray-500">Hot Work</p>
                            </div>
                          </div>
                        </div>
                        <p className="text-[10px] text-gray-500 text-center">Create & share your own project case studies</p>
                      </div>
                    </div>
                    <div className="absolute left-1/2 -translate-x-1/2 -bottom-2 w-4 h-4 bg-white rotate-45 border-r border-b border-gray-100"></div>
                  </div>
                </div>

                {/* 24/7 Access Box with Popup */}
                <div className="relative group">
                  <div className="bg-blue-700/50 rounded-xl p-6 text-center cursor-pointer transition-all duration-300 hover:bg-blue-600/60 hover:scale-105 hover:shadow-lg">
                    <Clock className="mx-auto mb-3 text-green-400" size={32} />
                    <p className="font-semibold">24/7 Access</p>
                    <p className="text-sm text-blue-200 mt-1">Always available</p>
                  </div>
                  {/* Popup */}
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-3 w-80 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 z-50">
                    <div className="bg-white rounded-xl shadow-2xl border border-gray-100 overflow-hidden">
                      <div className="bg-gradient-to-r from-blue-900 to-blue-800 px-4 py-3 flex items-center gap-2">
                        <img src="/bcs-logo.png" alt="BCS Logo" width="24" height="24" loading="lazy" className="h-6 w-auto" />
                        <span className="text-white font-semibold text-sm">Always-On Portal Access</span>
                      </div>
                      <div className="p-4">
                        <div className="flex items-center justify-center mb-3">
                          <div className="relative">
                            <div className="w-20 h-20 rounded-full border-4 border-blue-100 flex items-center justify-center">
                              <Clock className="text-blue-900" size={36} />
                            </div>
                            <div className="absolute -top-1 -right-1 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                              <CheckCircle className="text-white" size={14} />
                            </div>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-xs">
                          <div className="bg-gray-50 rounded-lg p-2 text-center">
                            <p className="font-semibold text-gray-900">Mobile Access</p>
                            <p className="text-gray-500 text-[10px]">Any device, anywhere</p>
                          </div>
                          <div className="bg-gray-50 rounded-lg p-2 text-center">
                            <p className="font-semibold text-gray-900">Instant Downloads</p>
                            <p className="text-gray-500 text-[10px]">Reports & documents</p>
                          </div>
                          <div className="bg-gray-50 rounded-lg p-2 text-center">
                            <p className="font-semibold text-gray-900">Live Support</p>
                            <p className="text-gray-500 text-[10px]">Emergency requests</p>
                          </div>
                          <div className="bg-gray-50 rounded-lg p-2 text-center">
                            <p className="font-semibold text-gray-900">Secure Portal</p>
                            <p className="text-gray-500 text-[10px]">Encrypted access</p>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="absolute left-1/2 -translate-x-1/2 -bottom-2 w-4 h-4 bg-white rotate-45 border-r border-b border-gray-100"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <Badge className="mb-4 bg-blue-100 text-blue-900 hover:bg-blue-100">About BCS</Badge>
          <h2 className="text-4xl font-bold text-slate-900 mb-6">
            Your Partner in Industrial Excellence
          </h2>
          <p className="!text-lg mb-6 text-gray-600">
            {mockCompanyInfo.description}
          </p>
          <p className="text-lg text-gray-600 mb-8">
            With decades of combined field experience, our team delivers repair and coating solutions that work the first time. We provide on-site technical support, application guidance, and emergency response services throughout California.
          </p>
          
          {/* Belzona Partnership */}
          <div className="bg-gradient-to-r from-blue-900 to-blue-800 rounded-2xl p-6 mb-8 text-white">
            <div className="flex flex-col md:flex-row items-center justify-center gap-4 mb-4">
              <Award className="text-amber-400" size={36} />
              <div className="text-center md:text-left">
                <h3 className="text-xl font-bold">Preferred Contractor of Belzona California Inc.</h3>
                <p className="text-blue-200">Certified applicators of world-leading polymer repair solutions</p>
              </div>
            </div>
            <p className="text-blue-100 text-sm max-w-2xl mx-auto mb-4">
              As the preferred Contractor of Belzona California Inc., BCS brings expert knowledge and certified application 
              techniques for cold-applied, in-situ repairs without hot work permits.
            </p>
            <div className="inline-block">
              <img 
                src="https://customer-assets.emergentagent.com/job_5438d55d-6fb3-446d-bfb9-defb744855cb/artifacts/64pdgdnx_orange-logo.png" 
                alt="Belzona California Inc. - Polymer repair solutions partner" 
                width="150"
                height="48"
                loading="lazy"
                className="h-12 bg-white rounded-lg px-4 py-2"
              />
            </div>
          </div>
          
          <div className="flex flex-wrap justify-center gap-4">
            <div className="bg-slate-50 rounded-lg p-6 text-center min-w-[200px]">
              <p className="!font-bold !text-3xl text-blue-900">
                <AnimatedCounter end={25} suffix="+" duration={1500} />
              </p>
              <p className="text-gray-600">Years Experience</p>
            </div>
            <div className="bg-slate-50 rounded-lg p-6 text-center min-w-[200px]">
              <p className="!font-bold !text-3xl text-blue-900">
                <AnimatedCounter end={1000} suffix="+" duration={2000} />
              </p>
              <p className="text-gray-600">Projects Completed</p>
            </div>
            <div className="bg-slate-50 rounded-lg p-6 text-center min-w-[200px]">
              <p className="text-3xl font-bold text-blue-900">
                <AnimatedCounter end={100} suffix="+" duration={1500} />
              </p>
              <p className="text-gray-600">Customers</p>
            </div>
            <div className="bg-slate-50 rounded-lg p-6 text-center min-w-[200px]">
              <p className="text-3xl font-bold text-blue-900">24/7</p>
              <p className="text-gray-600">Emergency Support</p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-900 to-blue-800">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-white/20 text-white border border-white/30 hover:bg-white/20">Customer Testimonials</Badge>
            <h2 className="text-4xl font-bold text-white mb-4">
              What Our Clients Say
            </h2>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto">
              Don't just take our word for it. Here's what our clients have to say about working with BCS.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {mockTestimonials.map((testimonial, index) =>
            <Card key={testimonial.id} className="hover:shadow-lg transition-shadow bg-white border-slate-200">
                <CardContent className="pt-6">
                  <div className="flex mb-4">
                    <Quote className="text-blue-900 opacity-20" size={48} />
                  </div>
                  <p className="text-gray-700 mb-6 italic min-h-[120px]">"<TypewriterText text={testimonial.quote} speed={20 + (index * 5)} />"</p>
                  <div className="border-t pt-4">
                    <p className="text-sm font-medium text-blue-900">{testimonial.role}</p>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </section>

      {/* Trusted by Industry Leaders */}
      <TrustedBy featuredOnly />

      {/* Research & Learn Section */}
      <section id="research-learn" className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-green-100 text-green-800 border border-green-200 hover:bg-green-100">Knowledge Center</Badge>
            <h2 className="text-4xl font-bold text-slate-900 mb-4">
              Research & Learn
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Explore common industrial problems and discover proven solutions.
            </p>
          </div>

          {/* Problem Categories Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            {/* Corrosion Problems */}
            <Card 
              className="hover:shadow-xl transition-all duration-300 border-2 border-transparent hover:border-green-500 cursor-pointer group"
              onClick={() => navigate('/research/corrosion-rust')}
              data-testid="research-card-corrosion"
            >
              <CardHeader>
                <div className="w-14 h-14 bg-red-100 rounded-xl flex items-center justify-center mb-4 group-hover:bg-red-200 transition-colors">
                  <Shield className="text-red-600" size={28} />
                </div>
                <CardTitle className="text-xl group-hover:text-green-700 transition-colors">Corrosion & Rust Issues</CardTitle>
                <CardDescription>Metal degradation, pitting, and surface deterioration</CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="text-green-500 mt-0.5 flex-shrink-0" size={16} />
                    <span>Tank and vessel corrosion</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="text-green-500 mt-0.5 flex-shrink-0" size={16} />
                    <span>Pipe wall thinning</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="text-green-500 mt-0.5 flex-shrink-0" size={16} />
                    <span>Structural steel degradation</span>
                  </li>
                </ul>
                <Button variant="link" className="text-green-600 p-0 mt-4 group-hover:text-green-700">
                  Learn about solutions <ArrowRight size={16} className="ml-1" />
                </Button>
              </CardContent>
            </Card>

            {/* Leak Problems */}
            <Card 
              className="hover:shadow-xl transition-all duration-300 border-2 border-transparent hover:border-green-500 cursor-pointer group"
              onClick={() => navigate('/research/leaks-seepage')}
              data-testid="research-card-leaks"
            >
              <CardHeader>
                <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center mb-4 group-hover:bg-blue-200 transition-colors">
                  <Droplets className="text-blue-600" size={28} />
                </div>
                <CardTitle className="text-xl group-hover:text-green-700 transition-colors">Leaks & Seepage</CardTitle>
                <CardDescription>Pipe leaks, tank seepage, and containment failures</CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="text-green-500 mt-0.5 flex-shrink-0" size={16} />
                    <span>Pinhole leaks in piping</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="text-green-500 mt-0.5 flex-shrink-0" size={16} />
                    <span>Flange and joint leaks</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="text-green-500 mt-0.5 flex-shrink-0" size={16} />
                    <span>Secondary containment issues</span>
                  </li>
                </ul>
                <Button variant="link" className="text-green-600 p-0 mt-4 group-hover:text-green-700">
                  Learn about solutions <ArrowRight size={16} className="ml-1" />
                </Button>
              </CardContent>
            </Card>

            {/* Erosion Problems */}
            <Card 
              className="hover:shadow-xl transition-all duration-300 border-2 border-transparent hover:border-green-500 cursor-pointer group"
              onClick={() => navigate('/research/erosion-wear')}
              data-testid="research-card-erosion"
            >
              <CardHeader>
                <div className="w-14 h-14 bg-amber-100 rounded-xl flex items-center justify-center mb-4 group-hover:bg-amber-200 transition-colors">
                  <Wind className="text-amber-600" size={28} />
                </div>
                <CardTitle className="text-xl group-hover:text-green-700 transition-colors">Erosion & Wear</CardTitle>
                <CardDescription>Surface wear from flow, abrasion, and cavitation</CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="text-green-500 mt-0.5 flex-shrink-0" size={16} />
                    <span>Pump impeller erosion</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="text-green-500 mt-0.5 flex-shrink-0" size={16} />
                    <span>Slurry line wear</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="text-green-500 mt-0.5 flex-shrink-0" size={16} />
                    <span>Cavitation damage</span>
                  </li>
                </ul>
                <Button variant="link" className="text-green-600 p-0 mt-4 group-hover:text-green-700">
                  Learn about solutions <ArrowRight size={16} className="ml-1" />
                </Button>
              </CardContent>
            </Card>

            {/* Equipment Failures */}
            <Card 
              className="hover:shadow-xl transition-all duration-300 border-2 border-transparent hover:border-green-500 cursor-pointer group"
              onClick={() => navigate('/research/equipment-failures')}
              data-testid="research-card-equipment"
            >
              <CardHeader>
                <div className="w-14 h-14 bg-purple-100 rounded-xl flex items-center justify-center mb-4 group-hover:bg-purple-200 transition-colors">
                  <Settings className="text-purple-600" size={28} />
                </div>
                <CardTitle className="text-xl group-hover:text-green-700 transition-colors">Equipment Failures</CardTitle>
                <CardDescription>Pumps, valves, and rotating equipment problems</CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="text-green-500 mt-0.5 flex-shrink-0" size={16} />
                    <span>Pump housing damage</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="text-green-500 mt-0.5 flex-shrink-0" size={16} />
                    <span>Valve seat wear</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="text-green-500 mt-0.5 flex-shrink-0" size={16} />
                    <span>Shaft and bearing issues</span>
                  </li>
                </ul>
                <Button variant="link" className="text-green-600 p-0 mt-4 group-hover:text-green-700">
                  Learn about solutions <ArrowRight size={16} className="ml-1" />
                </Button>
              </CardContent>
            </Card>

            {/* HVAC Problems */}
            <Card 
              className="hover:shadow-xl transition-all duration-300 border-2 border-transparent hover:border-green-500 cursor-pointer group"
              onClick={() => navigate('/research/hvac-cooling')}
              data-testid="research-card-hvac"
            >
              <CardHeader>
                <div className="w-14 h-14 bg-cyan-100 rounded-xl flex items-center justify-center mb-4 group-hover:bg-cyan-200 transition-colors">
                  <Flame className="text-cyan-600" size={28} />
                </div>
                <CardTitle className="text-xl group-hover:text-green-700 transition-colors">HVAC & Cooling Systems</CardTitle>
                <CardDescription>Chillers, cooling towers, and heat exchangers</CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="text-green-500 mt-0.5 flex-shrink-0" size={16} />
                    <span>Chiller tube failures</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="text-green-500 mt-0.5 flex-shrink-0" size={16} />
                    <span>Cooling tower deterioration</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="text-green-500 mt-0.5 flex-shrink-0" size={16} />
                    <span>Condenser corrosion</span>
                  </li>
                </ul>
                <Button variant="link" className="text-green-600 p-0 mt-4 group-hover:text-green-700">
                  Learn about solutions <ArrowRight size={16} className="ml-1" />
                </Button>
              </CardContent>
            </Card>

            {/* Structural Problems */}
            <Card 
              className="hover:shadow-xl transition-all duration-300 border-2 border-transparent hover:border-green-500 cursor-pointer group"
              onClick={() => navigate('/research/structural-damage')}
              data-testid="research-card-structural"
            >
              <CardHeader>
                <div className="w-14 h-14 bg-slate-200 rounded-xl flex items-center justify-center mb-4 group-hover:bg-slate-300 transition-colors">
                  <Building2 className="text-slate-600" size={28} />
                </div>
                <CardTitle className="text-xl group-hover:text-green-700 transition-colors">Structural Damage</CardTitle>
                <CardDescription>Concrete, steel, and foundation issues</CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="text-green-500 mt-0.5 flex-shrink-0" size={16} />
                    <span>Concrete spalling and cracks</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="text-green-500 mt-0.5 flex-shrink-0" size={16} />
                    <span>Steel beam corrosion</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="text-green-500 mt-0.5 flex-shrink-0" size={16} />
                    <span>Foundation deterioration</span>
                  </li>
                </ul>
                <Button variant="link" className="text-green-600 p-0 mt-4 group-hover:text-green-700">
                  Learn about solutions <ArrowRight size={16} className="ml-1" />
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* CTA Banner */}
          <div className="bg-gradient-to-r from-green-700 to-green-600 rounded-2xl p-8 md:p-12 text-center">
            <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">
              Can't Find Your Problem?
            </h3>
            <p className="text-green-100 text-lg mb-6 max-w-2xl mx-auto">
              Our technical experts can help diagnose your issue and recommend the best solution. Get a free consultation today.
            </p>
            <Button
              size="lg"
              className="bg-white hover:bg-gray-100 text-green-700 font-semibold px-8 shadow-lg"
              onClick={() => window.location.href = 'tel:+19493719948'}
            >
              Talk to an Expert
              <ArrowRight className="ml-2" size={20} />
            </Button>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-green-100 text-green-900 hover:bg-green-100">Contact Us</Badge>
            <h2 className="!font-bold !text-4xl mb-4 text-slate-900">Get In Touch Today

            </h2>
            <p className="!text-xl text-gray-600">Don't wait for corrosion to damage your assets.

            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Contact Form */}
            <Card>
              <CardHeader>
                <CardTitle>Request a Call from BCS</CardTitle>
                <CardDescription>Fill out the form and we'll respond within 4 hours</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Input
                      type="text"
                      name="name"
                      placeholder="Your Name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required />

                  </div>
                  <div>
                    <Input
                      type="email"
                      name="email"
                      placeholder="Email Address"
                      value={formData.email}
                      onChange={handleInputChange}
                      required />

                  </div>
                  <div>
                    <Input
                      type="tel"
                      name="phone"
                      placeholder="Phone Number"
                      value={formData.phone}
                      onChange={handleInputChange} />

                  </div>
                  <div>
                    <Input
                      type="text"
                      name="company"
                      placeholder="Company Name"
                      value={formData.company}
                      onChange={handleInputChange} />

                  </div>
                  <div>
                    <Textarea
                      name="message"
                      placeholder="Tell us about your project or repair needs..."
                      value={formData.message}
                      onChange={handleInputChange}
                      rows={4}
                      required />

                  </div>
                  <Button type="submit" className="w-full bg-blue-900 hover:bg-blue-800" disabled={isSubmitting}>
                    {isSubmitting ? 'Sending...' : 'Submit Request'}
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Contact Info */}
            <div className="space-y-6">
              <Card 
                className="cursor-pointer hover:shadow-lg transition-shadow border-2 hover:border-green-200"
                onClick={() => window.location.href = 'tel:+19493719948'}
              >
                <CardContent className="pt-6">
                  <div className="flex items-start mb-4">
                    <Phone className="text-green-600 mr-3 mt-1" size={20} />
                    <div>
                      <p className="font-semibold text-slate-900">Call Us</p>
                      <p className="text-gray-600">{mockCompanyInfo.phone}</p>
                      <p className="!text-sm text-gray-500">Click here to call us directly.</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card 
                className="cursor-pointer hover:shadow-lg transition-shadow border-2 hover:border-blue-200"
                onClick={() => window.location.href = 'mailto:info@bcs-us.com?subject=Service Inquiry'}
              >
                <CardContent className="pt-6">
                  <div className="flex items-start">
                    <Mail className="text-blue-900 mr-3 mt-1" size={20} />
                    <div>
                      <p className="font-semibold text-slate-900">Email Us</p>
                      <p className="!text-sm text-gray-500">Click here to send us an email directly.</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-start">
                    <Settings className="text-blue-900 mr-3 mt-1" size={20} />
                    <div>
                      <p className="font-semibold text-slate-900">Service Area</p>
                      <p className="text-gray-600">Nationwide Mobilization Capabilities </p>
                      <p className="!text-sm text-gray-500">On-site support always available.</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      </main>
      <Footer />

      {/* Success Modal */}
      <Dialog open={showSuccessModal} onOpenChange={setShowSuccessModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
              <CheckCircle2 className="h-10 w-10 text-green-600" />
            </div>
            <DialogTitle className="text-center text-xl">Request Submitted Successfully!</DialogTitle>
            <DialogDescription className="text-center text-base pt-2">
              Thank you for contacting BCS. We have received your request and will get back to you within <span className="font-semibold text-slate-900">4 hours</span>.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-center pt-4">
            <Button 
              onClick={() => setShowSuccessModal(false)}
              className="bg-blue-600 hover:bg-blue-700 px-8"
            >
              Got It
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>);};

export default Home;